import { useApolloClient } from "@apollo/client";
import { Avatar, Card, CardContent, CardHeader, CircularProgress, makeStyles, Snackbar, Theme, Typography } from "@material-ui/core";
import { red } from "@material-ui/core/colors";
import ErrorIcon from '@material-ui/icons/ErrorOutlineOutlined';
import LockIcon from '@material-ui/icons/LockOutlined';
import MuiAlert from '@material-ui/lab/Alert';
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useRouteMatch } from "react-router-dom";
import { InvitationCheckResult, useCheckInvitationQuery, useSaveTokenMutation, useValidateInvitationPinMutation } from "../../graphql/generated/graphql";
import PinCodeInput from "../../shared/PinCodeInput";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  progress: {
    padding: theme.spacing(2),
    marginLeft: "auto",
    marginRight: "auto",
    width: 40 + 2 * theme.spacing(2),
    height: 40 + 2 * theme.spacing(2),
  },
  enterPinCard: {
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: 600,
  },
  enterPinCardAvatar: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.getContrastText(theme.palette.background.paper),
  },
  errorCard: {
    marginLeft: "auto",
    marginRight: "auto",
    padding: theme.spacing(2),
    maxWidth: 400,
  },
  errorAvatar: {
    backgroundColor: red[600],
    color: theme.palette.getContrastText(red[600]),
  },
}));

export default function Invitation()
{
  const { t } = useTranslation();
  const classes = useStyles();
  const match = useRouteMatch<{ invitationCode: string }>("/invitation/:invitationCode");
  const [skip, setSkip] = React.useState(true);
  const { data: checkInvitationQueryData, loading: checkInvitationQueryLoading } = useCheckInvitationQuery({
    variables: { InvitationCode: match!.params.invitationCode },
    skip,
  });
  const [pin, setPin] = React.useState("");
  const [IsPinCodeInputEnabled, setIsPinCodeInputEnabled] = React.useState(true);
  const [showPinError, setShowPinError] = React.useState(false);
  const pinInputRef = React.useRef<HTMLInputElement | null>(null);
  const [validateInvitationPIN] = useValidateInvitationPinMutation();
  const [saveToken] = useSaveTokenMutation();
  const history = useHistory();
  const client = useApolloClient();


  useEffect(() =>
  {
    if(match?.params.invitationCode)
    {
      setTimeout(() =>
      {
        setSkip(false);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.params.invitationCode]);

  useEffect(() =>
  {
    if(checkInvitationQueryData?.CheckInvitation === InvitationCheckResult.EnterPin && IsPinCodeInputEnabled)
    {
      setTimeout(() =>
      {
        pinInputRef.current?.focus();
      }, 0);
    }
    if(checkInvitationQueryData?.CheckInvitation === InvitationCheckResult.Validated)
    {
      history.replace("/");
    }
  }, [checkInvitationQueryData, IsPinCodeInputEnabled, history]);

  useEffect(() =>
  {
    if(pin.length === 6)
    {
      setIsPinCodeInputEnabled(false);
      (async () =>
      {
        const result = await validateInvitationPIN({
          variables: {
            InvitationCode: match!.params.invitationCode,
            PINCode: pin
          },
        });
        if(result.data?.ValidateInvitationPIN.success)
        {
          await saveToken({ variables: { token: result.data.ValidateInvitationPIN.token! } });
          client.cache.evict({ fieldName: 'ListOrders' });
          client.cache.gc();
          history.replace('/');
        }
        else
        {
          setIsPinCodeInputEnabled(true);
          setPin("");
          setShowPinError(true);
          pinInputRef.current?.focus();
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  if(checkInvitationQueryLoading || skip) return <div className={classes.progress}><CircularProgress color="secondary" /></div>;
  if(checkInvitationQueryData?.CheckInvitation === InvitationCheckResult.Invalid) return (
    <div className={classes.root}>
      <Card className={classes.errorCard} elevation={3}>
        <CardHeader
          avatar={<Avatar className={classes.errorAvatar}><ErrorIcon /></Avatar>}
          title={<Typography variant="h5" component="h2">{t('partnerPortal.invitation.invalidCode')}</Typography>}
        />
      </Card>
    </div>
  );

  return (
    <div className={classes.root}>
      <Card className={classes.enterPinCard} elevation={3}>
        <CardHeader
          avatar={<Avatar className={classes.enterPinCardAvatar}><LockIcon /></Avatar>}
          title={<Typography variant="h5" component="h2">{t('partnerPortal.invitation.validCode')}</Typography>}
        />
        <CardContent>
          <Typography variant="body1" component="p">{t('partnerPortal.invitation.enterPin')}</Typography>
          <PinCodeInput value={pin} onChange={setPin} IsEnabled={IsPinCodeInputEnabled} inputRef={pinInputRef} />
        </CardContent>
      </Card>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showPinError}
        autoHideDuration={4000}
        onClose={() => { setShowPinError(false); }}
      >
        <MuiAlert elevation={6} variant="filled" severity="error">
          {t('partnerPortal.invitation.invalidPin')}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}
